
export function toText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

const ATTACHMENT_CONTEXT_MARKER_RE = /(^|\n)\s*(?:Attachment Context|Uploaded File Context)\s*(?:\(|:)/i;

export function stripAttachmentContextForDisplay(value: unknown): string {
  const text = toText(value).replace(/\r\n/g, '\n');
  const markerIndex = text.search(ATTACHMENT_CONTEXT_MARKER_RE);
  const displayText = markerIndex >= 0 ? text.slice(0, markerIndex) : text;
  return displayText.replace(/\n{3,}/g, '\n\n').trim();
}

export function hasLeadingMarkdownHeading(content: string): boolean {
  const firstLine = content
    .split(/\r?\n/)
    .find((line) => line.trim().length > 0);
  return /^#{1,6}\s+\S/.test(firstLine?.trim() || '');
}

export function compactProjectTitle(value: unknown): string {
  return toText(value)
    .split(/\r?\n/)[0]
    .replace(/^#{1,6}\s+/, '')
    .replace(/[`*_~>[\](){}]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 30);
}

export function getRequirementVariableName(point: any): string {
  const explicit = toText(point?.variableName).trim();
  if (explicit) return explicit;

  const normalized = toText(point?.normalizedName).trim();
  if (/^[a-z][A-Za-z0-9_]*$/.test(normalized)) return normalized;

  const name = toText(point?.name).trim();
  if (/^[a-z][A-Za-z0-9_]*$/.test(name)) return name;

  return '';
}

export function compactFilenamePart(value: unknown, maxLength?: number): string {
  let compacted = toText(value)
    .split(/\r?\n/)[0]
    .replace(/^#{1,6}\s+/, '')
    // 注意：_ 保留不动 — extractSummaryTldr 已预先剥离了 markdown 强调标记，
    // 且 structured fields (如 Motor_RUN) 用下划线作分隔符，不应被替换
    .replace(/[`*~>[\](){}]/g, ' ')
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // 在标点/空格处截断，保留"完整短句"
  if (maxLength && compacted.length > maxLength) {
    const breakChars = /[，。；！？、,;!?.\u2026\u2014\s]/g;
    let bestBreak = -1;
    let match;
    while ((match = breakChars.exec(compacted)) !== null) {
      if (match.index < maxLength) {
        bestBreak = match.index;
      } else {
        break;
      }
    }
    if (bestBreak > maxLength * 0.6) {
      compacted = compacted.slice(0, bestBreak);
    } else {
      compacted = compacted.slice(0, maxLength);
    }
    compacted = compacted.replace(/[\s，。；！？、,;!?.\u2026\u2014]+$/, '');
  }

  if (!compacted || /^[\s_]+$/.test(compacted)) {
    compacted = toText(value)
      .split(/\r?\n/)[0]
      .replace(/^#{1,6}\s+/, '')
      .replace(/[`*_~>[\](){}]/g, ' ')
      .replace(/[<>:"/\\|?*\u0000-\u001F]/g, ' ')
      .replace(/[^\p{L}\p{N}\u4e00-\u9fff]+/gu, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, maxLength ?? 60)
      .replace(/_+$/g, '');
  }

  return compacted;
}

export function cleanRegionLabel(tail: string): string {
  let label = tail.trim();

  label = label
    .replace(/^;\s*/, '')
    .replace(/^[:：\-–—]+\s*/, '')
    .replace(/^\/\/\s*/, '')
    .trim();

  const wrappedBlockComment = label.match(/^(?:\(\*|\/\*)\s*([\s\S]*?)\s*(?:\*\/|\*\))$/);
  if (wrappedBlockComment) {
    label = wrappedBlockComment[1].trim();
  }

  label = label
    .replace(/^END_REGION\b\s*/i, '')
    .replace(/^REGION\b\s*/i, '')
    .replace(/^;\s*/, '')
    .replace(/^[:：\-–—]+\s*/, '')
    .replace(/^\/\/\s*/, '')
    .trim();

  return label;
}

export function formatEndRegionLine(prefix: string, tail: string): string {
  const label = cleanRegionLabel(tail);
  return label ? `${prefix}END_REGION // ${label}` : `${prefix}END_REGION`;
}

export function formatRegionLine(prefix: string, tail: string): string {
  const label = cleanRegionLabel(tail);
  return label ? `${prefix}REGION ${label}` : `${prefix}REGION`;
}

export function normalizeSiemensSclForDownload(code: string): string {
  const newline = code.includes('\r\n') ? '\r\n' : '\n';

  return code
    .split(/\r?\n/)
    .map((line) => {
      const blockCommentedRegion = line.match(/^(\s*)(?:\(\*|\/\*)\s*(REGION|END_REGION)\b\s*([\s\S]*?)\s*(?:\*\/|\*\))\s*$/i);
      if (blockCommentedRegion) {
        const [, prefix, keyword, tail] = blockCommentedRegion;
        return keyword.toUpperCase() === 'END_REGION'
          ? formatEndRegionLine(prefix, tail)
          : formatRegionLine(prefix, tail);
      }

      const commentedRegion = line.match(/^(\s*)\/\/\s*(REGION|END_REGION)\b\s*(.*)$/i);
      if (commentedRegion) {
        const [, prefix, keyword, tail] = commentedRegion;
        return keyword.toUpperCase() === 'END_REGION'
          ? formatEndRegionLine(prefix, tail)
          : formatRegionLine(prefix, tail);
      }

      const endRegion = line.match(/^(\s*)END_REGION\b\s*(.*)$/i);
      if (endRegion) {
        return formatEndRegionLine(endRegion[1], endRegion[2]);
      }

      const region = line.match(/^(\s*)REGION\b\s*(.*)$/i);
      if (region) {
        return formatRegionLine(region[1], region[2]);
      }

      return line;
    })
    .join(newline);
}

export function buildProgramDownloadContent(code: string, filename: string): string {
  if (!/\.scl$/i.test(filename)) return code;

  // Normalize REGION/END_REGION markers. No BOM added — the target's encoding
  // is applied at the download boundary (sclDownloadBlob).
  return normalizeSiemensSclForDownload(code);
}

export function extractSummaryTldr(summary: string): string {
  const lines = summary.split('\n');
  let inTldr = false;
  for (const line of lines) {
    if (/^##\s+/.test(line)) {
      inTldr = /项目概要|TL;DR/i.test(line);
      continue;
    }
    if (inTldr) {
      // 优先使用 AI 显式输出的程序名称字段（5-15字，专为文件名优化）
      const nameMatch = line.match(/\*\*(?:程序名称|Program Name)\*\*\s*[：:]\s*([^。！？.\n!?]+)/i);
      if (nameMatch) {
        const name = nameMatch[1].replace(/[，；、,;]+$/, '').trim();
        if (name) return name;
      }

      let cleaned = line.trim()
        .replace(/[*_`~>#]/g, '')
        .replace(/^(\d+[\.\、\)]\s*)+/, '');

      // Strip filler prefixes that add noise to filenames
      cleaned = cleaned
        .replace(/^(这是一个|这个PLC程序|这个程序|这是|这是一个完整的)\s*/i, '')
        .replace(/^(This is a|This PLC program|This program)\s+/i, '');

      // Take only the first clause
      cleaned = cleaned.split(/[。，；！？\.、,;!?]/)[0].trim();

      if (cleaned) return cleaned;
    }
  }
  return '';
}

export function formatAttachmentSize(sizeBytes: number) {
  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
}
